// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect , useState } from 'react';
import { bool, func, object } from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import * as ReportsConstants from '../../../../constants/reports';
import * as ChartsConstants from '../../../../constants/charts';
import { API_BASED } from '../../../../constants/metrics';
import {
  fetchApiList,
  fetchAppList,
  fetchAppListForOrgInContext,
  fetchOrgList,
  fetchMetricsData,
} from '../../../../actions/metrics';
import { setUserMetricFilter, setUserDateFilter } from '../../../../actions/filterState';
import { getUserDetails } from '../../../../reducers/portalConfig';
import {
  getApiMap,
  getAppMap,
  getOrgMap,
} from '../../../../reducers/metrics';
import { getMetricsFiltersState, getDateFilterState } from '../../../../reducers/filterState';
import { getFeatureFlagOrgFilterForOrgPublishers } from '../../../../reducers/portalConfig';
import HLEReport from '../../../../components/Analytics/HLEReport';
import HitsChart from '../../CannedCharts/HLECharts/HitsChart';
import LatencyChart from '../../CannedCharts/HLECharts/LatencyChart';
import ErrorRateChart from '../../CannedCharts/HLECharts/ErrorRateChart';
import {
  FILTER_ENTITY_TYPE_API, FILTER_ENTITY_TYPE_APP, FILTER_ENTITY_TYPE_ORG } from '../../../../constants/filter';

const reportProps = {
  type: ReportsConstants.REPORT_HLE,
  charts: [
    {
      component: HitsChart,
      id: ChartsConstants.CHART_HLE_HITS,
    },
    {
      component: LatencyChart,
      id: ChartsConstants.CHART_HLE_LATENCY,
    },
    {
      component: ErrorRateChart,
      id: ChartsConstants.CHART_HLE_ERROR_RATE,
    },
  ],
};
const reportId = ReportsConstants.REPORT_HLE_SAVE;

const mapDispatchToProps = (dispatch) => ({
  fetchApiList: ( filterByName ) => dispatch(fetchApiList( { name: filterByName } )),
  fetchAppListForOrgInContext: () => dispatch(fetchAppListForOrgInContext()),
  fetchAppList: ( filterByName) =>
    dispatch(fetchAppList( { name: filterByName })),
  fetchOrgList: ( filterByName ) => dispatch(fetchOrgList( { name: filterByName } )),
  fetchMetricsData: (chartID, params, path, aggregation) =>
    dispatch(fetchMetricsData(chartID, params, path, aggregation, API_BASED)),
  setUserDateFilter: (userId, orgUuid, filterType, value) =>
    dispatch(setUserDateFilter(userId, reportId, orgUuid, filterType, value)),
  setUserMetricFilter: (userId, orgUuid,
    filterType, value) =>
    dispatch(setUserMetricFilter(userId, reportId, orgUuid, filterType, value)),
});

const mapStateToProps = (state) => {
  const userObj = getUserDetails(state) || {};
  const userId = userObj && userObj.uuid;
  const isFeatureFlagAnalyticsOrgPublisherEnabled = getFeatureFlagOrgFilterForOrgPublishers(state);
  const orgUuid = userObj.organizationUuid || '';
  const isDeveloper = userObj.developer;
  const isOrgAdmin = userObj.orgAdmin;
  const isOrgPublisher = userObj.orgPublisher;
  const hideOrgFilterForOrgPub = isOrgPublisher
    && !isFeatureFlagAnalyticsOrgPublisherEnabled;
  return {
    userId,
    orgUuid,
    isOrgPublisher,
    isFeatureFlagAnalyticsOrgPublisherEnabled,
    hideOrgFilterForOrgPub,
    isDeveloper,
    isOrgAdmin,
    apiMap: getApiMap(state),
    appMap: getAppMap(state),
    orgMap: getOrgMap(state),
    selectedDate: getDateFilterState(state, userId, reportId, orgUuid),
    selectedMetrics: getMetricsFiltersState(state, userId, reportId, orgUuid),
  };
};

const HLEReportPage = (props) => {
  const {
    fetchApiList,
    fetchOrgList,
    fetchAppList,
    fetchAppListForOrgInContext,
    hideOrgFilterForOrgPub,
    isDeveloper,
    isOrgAdmin,
    isOrgPublisher,
  } = props;
 

  const [filterByName, setFilterByName] = useState('');
  const [filterType, setFilterType] = useState('');
  useEffect(() => {
    if( filterType === FILTER_ENTITY_TYPE_API )
    {
      fetchApiList( filterByName );
    }
    else if( filterType === FILTER_ENTITY_TYPE_APP ) {
      if (isOrgPublisher) {
        fetchAppListForOrgInContext();
      } else {
        fetchAppList( filterByName );
      }
    }
    else if(filterType === FILTER_ENTITY_TYPE_ORG )
    {
        fetchOrgList( filterByName );
    }
  }, [filterByName, filterType]);

  useEffect(() => {
    fetchApiList();
    if (isOrgPublisher) {
      fetchAppListForOrgInContext();
    } else {
      fetchAppList( filterByName );
    }
    if (!isDeveloper && !isOrgAdmin && !hideOrgFilterForOrgPub) {
      fetchOrgList();
    }
  }, []);

  

  const onHandleSearchFilterNameChange = ( value ) => {
    setFilterByName(value);
  };

  const onEntityTypeChange = ( value ) => {
    setFilterType(value);
    setFilterByName("");
  };

  return (<HLEReport
    {...props}
    charts={reportProps.charts}
    type={reportProps.type}
    handleSearchFilterNameChange={onHandleSearchFilterNameChange}
    onEntityTypeChange={onEntityTypeChange}
  />);
};

HLEReportPage.propTypes = {
  fetchApiList: func,
  fetchAppList: func,
  fetchAppListForOrgInContext: func,
  fetchOrgList: func,
  filterState: object,
  hideOrgFilterForOrgPub: bool,
  isDeveloper: bool,
  isOrgAdmin: bool,
  isOrgPublisher: bool,
  isFeatureFlagAnalyticsOrgPublisherEnabled: bool,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
)(HLEReportPage);

